/**
 * useWebSocket - Real-time updates hook
 * Supports WebSocket, Server-Sent Events, and fallback polling
 * Updated to work with the refactored backend WebSocket endpoints
 */
import { useEffect, useRef, useState, useCallback } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Get WebSocket URL from API URL
const getWebSocketUrl = (path = '/ws') => {
  const wsUrl = API_URL.replace(/^http/, 'ws');
  return `${wsUrl}/api${path}`;
};

const WS_RECONNECT_DELAY = 3000;
const WS_MAX_RECONNECTS = 5;

/**
 * Main WebSocket hook with fallback support
 */
export const useWebSocket = (eventId, options = {}) => {
  const {
    channels = ['all'],
    onMessage,
    onConnect,
    onDisconnect,
    onError,
    autoReconnect = true,
    reconnectInterval = 3000,
    pollInterval = 2000,
    usePolling = false, // Default to WebSocket now
    userType = 'anonymous',
  } = options;

  const [isConnected, setIsConnected] = useState(false);
  const [lastMessage, setLastMessage] = useState(null);
  const [error, setError] = useState(null);
  const [connectionId, setConnectionId] = useState(null);
  const wsRef = useRef(null);
  const eventSourceRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectCountRef = useRef(0);
  const lastEventIdRef = useRef('0');

  // Store callbacks in refs to avoid re-creating effects
  const callbacksRef = useRef({ onMessage, onConnect, onDisconnect, onError });
  callbacksRef.current = { onMessage, onConnect, onDisconnect, onError };

  // WebSocket implementation
  const connectWebSocket = useCallback(() => {
    if (!eventId && !options.global) return;

    // Close existing connection
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    const path = eventId ? `/ws/event/${eventId}` : '/ws';
    const url = `${getWebSocketUrl(path)}?user_type=${userType}`;
    console.log('[WS] Connecting to:', url);

    try {
      const ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        console.log('[WS] Connected');
        setIsConnected(true);
        setError(null);
        reconnectCountRef.current = 0;
        callbacksRef.current.onConnect?.();
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          setLastMessage(data);

          // Handle connection confirmation
          if (data.type === 'connected' && data.connection_id) {
            setConnectionId(data.connection_id);
          }

          callbacksRef.current.onMessage?.(data);
        } catch (err) {
          console.error('[WS] Failed to parse message:', err);
        }
      };

      ws.onclose = (event) => {
        console.log('[WS] Disconnected:', event.code, event.reason);
        setIsConnected(false);
        setConnectionId(null);
        callbacksRef.current.onDisconnect?.(event);

        // Auto-reconnect
        if (
          autoReconnect &&
          reconnectCountRef.current < WS_MAX_RECONNECTS &&
          event.code !== 1000
        ) {
          reconnectCountRef.current += 1;
          console.log(`[WS] Reconnecting (${reconnectCountRef.current}/${WS_MAX_RECONNECTS})...`);
          reconnectTimeoutRef.current = setTimeout(() => {
            // Reconnect will be triggered on next render
            setIsConnected(false);
          }, WS_RECONNECT_DELAY);
        }
      };

      ws.onerror = (err) => {
        console.error('[WS] Error:', err);
        setError('WebSocket connection error');
        callbacksRef.current.onError?.(err);
      };
    } catch (err) {
      console.error('[WS] Connection failed:', err);
      setError(err.message);
      callbacksRef.current.onError?.(err);
    }
  }, [eventId, userType, autoReconnect, options.global]);

  const disconnectWebSocket = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (wsRef.current) {
      wsRef.current.close(1000, 'User disconnected');
      wsRef.current = null;
    }
    setIsConnected(false);
    setConnectionId(null);
  }, []);

  // Send message via WebSocket
  const sendMessage = useCallback((message) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message));
      return true;
    }
    console.warn('[WS] Cannot send - not connected');
    return false;
  }, []);

  // Ping to keep connection alive
  const ping = useCallback(() => {
    return sendMessage({ type: 'ping' });
  }, [sendMessage]);

  // Subscribe to a channel
  const subscribe = useCallback((channel) => {
    return sendMessage({ type: 'subscribe', channel });
  }, [sendMessage]);

  // Unsubscribe from a channel
  const unsubscribe = useCallback((channel) => {
    return sendMessage({ type: 'unsubscribe', channel });
  }, [sendMessage]);

  // Get live viewer count
  const getLiveCount = useCallback(() => {
    return sendMessage({ type: 'get_live_count' });
  }, [sendMessage]);

  // Polling implementation (fallback)
  const startPolling = useCallback(() => {
    if (!eventId) return;

    setIsConnected(true);
    callbacksRef.current.onConnect?.();

    const poll = async () => {
      try {
        const channelParam = Array.isArray(channels) ? channels.join(',') : channels;
        const response = await fetch(
          `${API_URL}/api/events/${eventId}/ws/poll?last_event_id=${lastEventIdRef.current}&channels=${channelParam}`
        );
        
        if (!response.ok) throw new Error('Poll failed');
        
        const data = await response.json();
        
        if (data.events && data.events.length > 0) {
          data.events.forEach((event) => {
            setLastMessage(event);
            callbacksRef.current.onMessage?.(event);
          });
          lastEventIdRef.current = data.last_event_id;
        }
        
        setError(null);
      } catch (err) {
        console.error('Polling error:', err);
        setError(err.message);
        callbacksRef.current.onError?.(err);
      }
    };

    // Initial poll
    poll();
    
    // Start interval
    pollIntervalRef.current = setInterval(poll, pollInterval);
  }, [eventId, channels, pollInterval]);

  const stopPolling = useCallback(() => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
    setIsConnected(false);
    callbacksRef.current.onDisconnect?.();
  }, []);

  // SSE implementation
  const connectSSE = useCallback(() => {
    if (!eventId) return;

    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    const channelParam = Array.isArray(channels) ? channels.join(',') : channels;
    const url = `${API_URL}/api/events/${eventId}/ws/stream?channels=${channelParam}`;

    try {
      const eventSource = new EventSource(url);
      eventSourceRef.current = eventSource;

      eventSource.onopen = () => {
        setIsConnected(true);
        setError(null);
        callbacksRef.current.onConnect?.();
      };

      eventSource.onerror = (e) => {
        setIsConnected(false);
        setError('Connection error');
        callbacksRef.current.onError?.(e);

        if (autoReconnect) {
          reconnectTimeoutRef.current = setTimeout(() => {
            connectSSE();
          }, reconnectInterval);
        }
      };

      eventSource.addEventListener('connected', (e) => {
        const data = JSON.parse(e.data);
        setIsConnected(true);
        callbacksRef.current.onConnect?.(data);
      });

      eventSource.addEventListener('heartbeat', () => {
        // Heartbeat - connection alive
      });

      eventSource.onmessage = (e) => {
        try {
          const data = JSON.parse(e.data);
          setLastMessage(data);
          callbacksRef.current.onMessage?.(data);
        } catch (err) {
          console.error('Failed to parse message:', err);
        }
      };

      // Event-specific handlers
      const eventTypes = [
        'delegate.registered', 'delegate.checked_in',
        'session.started', 'session.ended',
        'stage.live', 'stage.ended',
        'chat.message', 'question.submitted', 'question.approved',
        'speaker.added', 'sponsor.added', 'exhibitor.added',
        'announcement', 'notification',
        'check_in', 'registration', 'stats_update', 'session_update',
      ];

      eventTypes.forEach((eventType) => {
        eventSource.addEventListener(eventType, (e) => {
          try {
            const data = JSON.parse(e.data);
            setLastMessage({ type: eventType, ...data });
            callbacksRef.current.onMessage?.({ type: eventType, ...data });
          } catch (err) {
            console.error('Failed to parse event:', eventType, err);
          }
        });
      });
    } catch (err) {
      console.error('SSE connection failed:', err);
      setError(err.message);
      callbacksRef.current.onError?.(err);
      
      // Fall back to polling
      if (usePolling) {
        startPolling();
      }
    }
  }, [eventId, channels, autoReconnect, reconnectInterval, usePolling, startPolling]);

  const disconnectSSE = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
    setIsConnected(false);
    callbacksRef.current.onDisconnect?.();
  }, []);

  // Connect on mount
  useEffect(() => {
    if (usePolling) {
      startPolling();
    } else {
      // Try WebSocket first
      connectWebSocket();
    }

    return () => {
      disconnectWebSocket();
      disconnectSSE();
      stopPolling();
    };
  }, [eventId, usePolling, connectWebSocket, startPolling]);

  return {
    isConnected,
    lastMessage,
    error,
    connectionId,
    sendMessage,
    ping,
    subscribe,
    unsubscribe,
    getLiveCount,
    reconnect: usePolling ? startPolling : connectWebSocket,
    disconnect: usePolling ? stopPolling : disconnectWebSocket,
  };
};

/**
 * Hook for event-specific WebSocket connection
 */
export const useEventWebSocket = (eventId, options = {}) => {
  const {
    onCheckIn,
    onRegistration,
    onStatsUpdate,
    onNotification,
    onSessionUpdate,
    ...restOptions
  } = options;

  const [liveCount, setLiveCount] = useState(0);

  const handleMessage = useCallback((data) => {
    switch (data.type) {
      case 'check_in':
      case 'delegate.checked_in':
        onCheckIn?.(data);
        break;
      case 'registration':
      case 'delegate.registered':
        onRegistration?.(data);
        break;
      case 'stats_update':
        onStatsUpdate?.(data.stats);
        break;
      case 'notification':
      case 'announcement':
        onNotification?.(data);
        break;
      case 'session_update':
      case 'session.started':
      case 'session.ended':
        onSessionUpdate?.(data);
        break;
      case 'live_count':
        setLiveCount(data.count);
        break;
      default:
        break;
    }
  }, [onCheckIn, onRegistration, onStatsUpdate, onNotification, onSessionUpdate]);

  const ws = useWebSocket(eventId, {
    ...restOptions,
    onMessage: (data) => {
      handleMessage(data);
      restOptions.onMessage?.(data);
    },
  });

  return {
    ...ws,
    liveCount,
  };
};

/**
 * Hook for admin WebSocket connection
 */
export const useAdminWebSocket = (options = {}) => {
  const {
    onCheckIn,
    onRegistration,
    onIncident,
    onNotification,
    ...restOptions
  } = options;

  const [stats, setStats] = useState(null);

  const handleMessage = useCallback((data) => {
    switch (data.type) {
      case 'check_in':
        onCheckIn?.(data);
        break;
      case 'registration':
        onRegistration?.(data);
        break;
      case 'incident':
        onIncident?.(data);
        break;
      case 'notification':
        onNotification?.(data);
        break;
      case 'stats':
        setStats(data.data);
        break;
      default:
        break;
    }
  }, [onCheckIn, onRegistration, onIncident, onNotification]);

  const ws = useWebSocket(null, {
    ...restOptions,
    global: true,
    userType: 'admin',
    onMessage: (data) => {
      handleMessage(data);
      restOptions.onMessage?.(data);
    },
  });

  const getStats = useCallback(() => {
    ws.sendMessage({ type: 'get_stats' });
  }, [ws]);

  const broadcast = useCallback((message, target = 'global', eventId = null) => {
    ws.sendMessage({
      type: 'broadcast',
      message,
      target,
      event_id: eventId,
    });
  }, [ws]);

  return {
    ...ws,
    stats,
    getStats,
    broadcast,
  };
};

export default useWebSocket;
